/**
 * Reels de Instagram — carga diferida del embed oficial.
 *
 * Por qué no un <video> propio: Instagram no expone la URL del mp4 ni una
 * miniatura estable sin token de la Graph API. El embed oficial
 * (/p/<code>/embed/captioned/) es público, no pide login y trae la portada
 * y el reproductor. Su costo es peso: ~600 KB de terceros por video.
 *
 * Por eso cada tarjeta arranca como una portada liviana propia y el iframe
 * entra recién cuando hace falta:
 *   - sin poster propio → al acercarse al viewport (se ve la portada real
 *     de Instagram sin que el usuario tenga que hacer nada);
 *   - con poster propio → al hacer click (cero terceros hasta entonces).
 *
 * Sin JS, la portada queda como enlace al post: nada se rompe.
 */

import { VIDEOS, VIDEOS_MODE } from '../config.js';

const EMBED_BASE = 'https://www.instagram.com/p/';
/** Cuánto antes del viewport empezamos a cargar. */
const PRELOAD_MARGIN = '400px';

function embedUrl(code) {
  return `${EMBED_BASE}${encodeURIComponent(code)}/embed/`;
}

function mountEmbed(card) {
  if (card.dataset.loaded === 'true') return;
  card.dataset.loaded = 'true';

  const code = card.dataset.videoCode;
  const frame = card.querySelector('[data-video-frame]');
  const cover = card.querySelector('[data-video-cover]');
  if (!code || !frame) return;

  const iframe = document.createElement('iframe');
  iframe.src = embedUrl(code);
  iframe.title = card.dataset.videoTitle || 'Reel de Serendipia en Instagram';
  iframe.loading = 'lazy';
  iframe.allow = 'autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share';
  iframe.allowFullscreen = true;
  iframe.setAttribute('scrolling', 'no');
  iframe.setAttribute('frameborder', '0');
  iframe.referrerPolicy = 'origin-when-cross-origin';

  // La portada se apaga cuando el iframe terminó de cargar, no antes:
  // así no queda un hueco vacío si Instagram tarda.
  iframe.addEventListener('load', () => cover?.classList.add('is-hidden'), { once: true });

  frame.prepend(iframe);
}

export function init() {
  const cards = document.querySelectorAll('[data-video-code]');
  if (!cards.length) return;

  const posterByCode = new Map(VIDEOS.map((v) => [v.code, v.poster]));
  const reducedData = navigator.connection?.saveData === true;

  cards.forEach((card) => {
    const cover = card.querySelector('[data-video-cover]');
    const hasPoster = Boolean(posterByCode.get(card.dataset.videoCode));

    // Click en la portada: siempre carga el embed, tenga poster o no.
    cover?.addEventListener('click', (e) => {
      e.preventDefault();
      mountEmbed(card);
    });

    // En modo 'click', con poster propio, o si el usuario pidió ahorrar
    // datos, esperamos el click y no cargamos nada de Instagram.
    if (VIDEOS_MODE === 'click' || hasPoster || reducedData) return;

    const io = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          mountEmbed(card);
        });
      },
      { rootMargin: PRELOAD_MARGIN }
    );

    io.observe(card);
  });
}
